import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import Loading from '../../components/Loading';
import {
  CreatePaymentSessionInput,
  GetInvoiceSuccess,
  PaymentMethod,
  PaymentSessionResult,
  useCreatePaymentSessionMutation,
  useGetPaymentRouteDeciderQuery,
  UserPaymentPlanOption
} from '../../generated/graphql';
import { QuickComplete } from '../Complete/Complete';
import { PaymentCompleteMessage } from '../Complete/TnCSignedFlow';
import InvoiceDetails from './InvoiceDetails/InvoiceDetails';
import PaymentOptions from './PaymentOptions/PaymentOptions';
import PaymentPlanOptions from './PaymentPlanOptions/PaymentPlanOptions';
import PaymentRedirectModal from './PaymentRedirectModal/PaymentRedirectModal';
import ProceedButton from './ProceedButton/ProceedButton';
import UnableToChargeModal from './UnableToChargeModal/UnableToChargeModal';

export interface InitiatePaymentParams {
  invoiceId: string;
}

const CANADA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon'
];

export const SimpleQuoteExpired: React.FC = () => (
  <div className="m-auto text-lg text-center">Your quote has expired</div>
);

const InitiatePayment: React.FC = () => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CreditCard
  );
  const [
    paymentPlanOption,
    setPaymentPlanOption
  ] = useState<UserPaymentPlanOption>();
  const [purchaseStartTime, setPurchaseStartTime] = useState<number | null>(
    null
  );
  const [isOpenModal, setOpenModel] = useState(false);
  const [
    disableProceedButtonOnceClick,
    setDisableProceedButtonOnceClick
  ] = useState(false);
  const [useCardForRenewals, setUseCardForRenewals] = useState(true);
  const [country, setCountry] = useState('');
  const { invoiceId } = useParams<InitiatePaymentParams>();
  const { data, loading, error } = useGetPaymentRouteDeciderQuery({
    variables: { invoiceId: invoiceId }
  });
  const [
    createPaymentSessionExpired,
    setCreatePaymentSessionExpired
  ] = useState(false);
  const [
    paymentSessionResult,
    setPaymentSessionResult
  ] = useState<PaymentSessionResult>();
  const [createPaymentSession] = useCreatePaymentSessionMutation();

  const fetchPaymentSession = useCallback(async () => {
    if (paymentPlanOption) {
      const createPaymentSessionInput: CreatePaymentSessionInput = {
        invoiceId,
        paymentPlanId: paymentPlanOption.paymentPlanId,
        method: paymentMethod,
        useCardForRenewals
      };
      const { data: createPaymentData } = await createPaymentSession({
        variables: {
          isContinuing: false,
          paymentInfo: createPaymentSessionInput
        }
      });
      setDisableProceedButtonOnceClick(false);
      if (!createPaymentData) {
        throw new Error('createPaymentData should be ready');
      }
      if (
        ['CancelledRenewalData', 'CompletionData'].includes(
          createPaymentData.createPaymentSession.__typename
        )
      ) {
        setCreatePaymentSessionExpired(true);
      } else {
        setPaymentSessionResult(createPaymentData.createPaymentSession);
      }
    }
  }, [
    paymentPlanOption,
    invoiceId,
    paymentMethod,
    useCardForRenewals,
    createPaymentSession,
    setPaymentSessionResult
  ]);

  const onClickProceedButtonForChargeSuccess = useCallback(() => {
    setDisableProceedButtonOnceClick(true);
    setOpenModel(true);
    fetchPaymentSession();
  }, [fetchPaymentSession]);

  const onClickProceedButtonForNewBusinessAndRenewal = useCallback(() => {
    setDisableProceedButtonOnceClick(true);
    setOpenModel(true);
    fetchPaymentSession();
  }, [fetchPaymentSession]);

  useEffect(() => {
    setPurchaseStartTime(new Date().getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      // data?.getInvoice.__typename === 'GetInvoiceSuccess' &&
      // data.getInvoice.invoiceInfo
      data?.paymentRouteDecider.__typename === 'InitialSuccess' &&
      data.paymentRouteDecider.getInvoiceDeciderResult.__typename ===
        'GetInvoiceSuccess'
    ) {
      const {
        invoiceInfo: { client },
        useCardForRenewals
      } = data.paymentRouteDecider.getInvoiceDeciderResult as GetInvoiceSuccess;
      if (typeof useCardForRenewals === 'boolean') {
        setUseCardForRenewals(useCardForRenewals);
      }

      if (client) {
        const { region: stateName } = client.address;
        const countryName = CANADA_PROVINCES.includes(stateName)
          ? 'Canada'
          : 'United States of America';
        setCountry(countryName);
      }
    }
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  if (createPaymentSessionExpired) {
    return <SimpleQuoteExpired />;
  }

  if (error || !data) {
    if (purchaseStartTime && timeout(purchaseStartTime)) {
      //TODO: delete it
      return <QuickComplete />;
    } else {
      return <Redirect to="/404" />;
    }
  }

  const { paymentRouteDecider: paymentRouteDeciderData } = data;

  switch (paymentRouteDeciderData.__typename) {
    case 'FoxdenError': {
      return <PaymentCompleteMessage isNotFound />;
    }
    case 'IsExpired': {
      return <PaymentCompleteMessage isExpired />;
    }
    case 'CancelledRenewalData': {
      return <SimpleQuoteExpired />;
    }
    case 'IsPaid': {
      const { policyNumber } = paymentRouteDeciderData;
      return <PaymentCompleteMessage policyFoxdenId={policyNumber} />;
    }
    case 'WaiveSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceId}`,
            state: { flowKind: 'WaiveSuccess' }
          }}
        />
      );
    }
    case 'RefundFullPaySuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceId}`,
            state: { flowKind: 'RefundFullPaySuccess' }
          }}
        />
      );
    }
    case 'RefundInstallmentSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceId}`,
            state: { flowKind: 'RefundInstallmentSuccess' }
          }}
        />
      );
    }
    case 'CancellationSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceId}`,
            state: { flowKind: 'CancellationSuccess' }
          }}
        />
      );
    }
    case 'InitialSuccess': {
      if (isOpenModal) {
        if (!paymentPlanOption) {
          throw Error('paymentPlanOption is not selected');
        }
        if (paymentSessionResult?.__typename === 'ActiveRenewalFailureData') {
          return (
            <UnableToChargeModal
              activeRenewalFailureData={paymentSessionResult}
              paymentMethod={paymentMethod}
            />
          );
        }
        return (
          <PaymentRedirectModal
            paymentMethod={paymentMethod}
            paymentSessionResult={paymentSessionResult}
            setClose={() => setOpenModel(false)}
          />
        );
      }
      const {
        userPaymentPlanOptionDeciderResult: {
          userPaymentPlanOption: userPlanData
        }
      } = paymentRouteDeciderData;
      return (
        <div
          className="m-auto text:lg sm:text-xl"
          data-testid="InitiatePayment"
        >
          <div className="container">
            <div className="uppercase text-left p-2 text-2xl bg-secondary rounded-md border-none font-bold">
              Complete Payment
            </div>
            <div className="text-xl my-4">
              Please choose a payment option and complete your payment
            </div>
            <div className="grid">
              <div className="col-span-1 uppercase text-left p-2 text-lg bg-gray-400 rounded-md border-none font-bold mb-4">
                Billing Information
              </div>
              {paymentPlanOption ? (
                <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                  <InvoiceDetails
                    paymentMethod={paymentMethod}
                    paymentPlanOption={paymentPlanOption}
                    isInitial={true}
                    country={country}
                  />
                </div>
              ) : null}
              <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                <PaymentOptions
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                />
              </div>
              <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                <PaymentPlanOptions
                  userPlanData={userPlanData}
                  paymentPlanOption={paymentPlanOption}
                  setPaymentPlanOption={setPaymentPlanOption}
                  useCardForRenewals={useCardForRenewals}
                  setUseCardForRenewals={setUseCardForRenewals}
                />
              </div>
            </div>
            {paymentMethod !== undefined ? (
              <div className="text-center my-4">
                {disableProceedButtonOnceClick ? null : ( // TODO: Prefer disabling the button over hiding it — hiding might cause confusion.
                  <ProceedButton
                    paymentMethod={paymentMethod}
                    onClick={onClickProceedButtonForNewBusinessAndRenewal}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    case 'ChargeSuccess': {
      if (isOpenModal) {
        if (!paymentPlanOption) {
          throw Error('paymentPlanOption is not selected');
        }
        return (
          <PaymentRedirectModal
            paymentMethod={paymentMethod}
            paymentSessionResult={paymentSessionResult}
            setClose={() => setOpenModel(false)}
          />
        );
      }
      const {
        userPaymentPlanOptionDeciderResult: {
          userPaymentPlanOption: userPlanData
        }
      } = paymentRouteDeciderData;
      return (
        <div
          className="InitiatePayment m-auto text:lg sm:text-xl"
          data-testid="InitiatePayment"
        >
          <div className="container">
            <div className="uppercase text-left p-2 text-2xl bg-secondary rounded-md border-none font-bold">
              Complete Payment
            </div>
            <div className="text-xl my-4">
              Please choose a payment option and complete your payment
            </div>
            <div className="grid">
              <div className="col-span-1 uppercase text-left p-2 text-lg bg-gray-400 rounded-md border-none font-bold mb-4">
                Billing Information
              </div>
              {paymentPlanOption ? (
                <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                  <InvoiceDetails
                    paymentMethod={paymentMethod}
                    paymentPlanOption={paymentPlanOption}
                    isInitial={false}
                    country={country}
                  />
                </div>
              ) : null}
              <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                <PaymentOptions
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                />
              </div>
              <div className="pl-10 sm:row-start-2 sm:col-span-auto">
                <PaymentPlanOptions
                  userPlanData={userPlanData}
                  paymentPlanOption={paymentPlanOption}
                  setPaymentPlanOption={setPaymentPlanOption}
                  useCardForRenewals={useCardForRenewals}
                  setUseCardForRenewals={setUseCardForRenewals}
                />
              </div>
            </div>
            {paymentMethod !== undefined ? (
              <div className="text-center my-4">
                {disableProceedButtonOnceClick ? null : ( // TODO: Prefer disabling the button over hiding it — hiding might cause confusion.
                  <ProceedButton
                    paymentMethod={paymentMethod}
                    onClick={onClickProceedButtonForChargeSuccess}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    default:
      return <PaymentCompleteMessage isNotFound />;
  }
};

const timeout = (startTime: number): boolean => {
  // Timeout of lambda function is 29 seconds.
  const timeLimit = 28.9 * 1000;
  const currentTime = new Date().getTime();
  //TODO delete it
  if (startTime + timeLimit < currentTime) {
    return true;
  }

  return false;
};

export default InitiatePayment;
